import {
  type BagEntry,
  type ClassId,
  getItem,
  type ItemInstance,
  type ItemTemplate,
} from '@shefira/shared';

import type { CssAtlas } from '../assets/dom-atlas.js';
import { addLevelBadge, paintItemIcon } from './item-icon.js';
import { ItemTooltip, type TooltipAction } from './item-tooltip.js';
import { stashCells, stashMoveBlock, type StashSide, type StashTarget } from './stash-moves.js';
import { uiFormat, uiNumber, uiText } from './text.js';
import { isTouch } from './touch.js';
import './inventory.css';

/**
 * One side of the chest's window (ADR 0079) as the bag's grid of cases
 * (inventory-panel.ts): an item is dragged to the other grid, or
 * double-clicked to send its whole stack over; a click picks it for the
 * panel's bar, which doses part of a stack. On a phone a tap pins its card
 * with « Déposer » / « Retirer ». What the server would refuse is greyed;
 * the server judges every move all the same.
 */

/** The side rides in the type: `dragover` sees the types, never the data. */
function dragType(side: StashSide): string {
  return `application/x-shefira-stash-${side}`;
}

export interface StashGridView {
  icons: CssAtlas | null;
  /** What this side holds. */
  entries: readonly BagEntry[];
  capacity: number;
  /** Where its items would go: the other side's entries and size. */
  target: StashTarget;
  inReach: boolean;
  wearer: { level: number; classId: ClassId };
}

export interface StashGridHandlers {
  /** Sends `quantity` of one of this side's items to the other side. */
  onMove: (itemId: string, quantity: number) => void;
  /** An item of the other side was dropped here: the panel sends it over. */
  onReceive: (itemId: string) => void;
  /** The picked item changed (a click, a tap). */
  onSelect: () => void;
}

export interface StashPick {
  item: ItemInstance;
  template: ItemTemplate;
}

export class StashGrid {
  readonly element: HTMLElement;
  private readonly tooltip = new ItemTooltip();
  private view: StashGridView | undefined;
  /** The picked item's id; it stays picked across redraws while it is still here. */
  private selected: string | null = null;

  constructor(
    readonly side: StashSide,
    private readonly handlers: StashGridHandlers,
  ) {
    this.element = document.createElement('div');
    this.element.className = 'bag stash__grid';
    this.element.dataset.side = side;
    this.bindDropTarget();
  }

  /** The picked item and its template, or null. */
  get pick(): StashPick | null {
    if (this.selected === null || this.view === undefined) return null;
    const entry = this.view.entries.find(({ item }) => item.id === this.selected);
    const template = entry === undefined ? undefined : getItem(entry.item.templateId);
    return entry === undefined || template === undefined ? null : { item: entry.item, template };
  }

  /** Lets go of the picked item, without a redraw. */
  unselect(): void {
    this.selected = null;
    this.element.querySelector('.item--selected')?.classList.remove('item--selected');
  }

  /** The window closed: nothing picked, no card. */
  reset(): void {
    this.unselect();
    this.hide();
  }

  hide(): void {
    this.tooltip.hide();
  }

  destroy(): void {
    this.tooltip.destroy();
  }

  render(view: StashGridView): void {
    this.view = view;
    if (this.pick === null) this.selected = null;
    const scroll = this.element.scrollTop;
    this.element.replaceChildren();
    const bySlot = new Map(view.entries.map((entry) => [entry.slot, entry.item]));
    const cells = stashCells(view.entries, view.capacity);
    for (let slot = 0; slot < cells; slot += 1) {
      const cell = document.createElement('div');
      cell.className = 'slot';
      const item = bySlot.get(slot);
      if (item !== undefined) cell.append(this.itemElement(item, view));
      this.element.append(cell);
    }
    this.element.scrollTop = scroll;
  }

  /** Sends one of this side's items over whole, unless the server would refuse it. */
  moveWhole(itemId: string): void {
    const view = this.view;
    const item = view?.entries.find((entry) => entry.item.id === itemId)?.item;
    const template = item === undefined ? undefined : getItem(item.templateId);
    if (view === undefined || item === undefined || template === undefined) return;
    if (stashMoveBlock(this.side, item, template, item.quantity, view.target, view.inReach)) {
      return;
    }
    this.handlers.onMove(item.id, item.quantity);
  }

  private itemElement(item: ItemInstance, view: StashGridView): HTMLElement {
    const template = getItem(item.templateId);
    const element = document.createElement('div');
    element.className = `item item--${template?.rarity ?? 'common'}`;
    element.dataset.item = item.id;
    paintItemIcon(element, template, view.icons, item.templateId);
    addLevelBadge(element, item.upgradeLevel);
    if (item.quantity > 1) {
      const quantity = document.createElement('span');
      quantity.className = 'item__qty';
      quantity.textContent = String(item.quantity);
      element.append(quantity);
    }
    if (item.id === this.selected) element.classList.add('item--selected');
    if (template === undefined) {
      element.draggable = false;
      return element;
    }
    const block = stashMoveBlock(this.side, item, template, item.quantity, view.target, true);
    const stuck = block === 'bound' || block === 'wardrobe';
    if (stuck) element.classList.add('item--unpickable');
    // A finger has no drag and drop (iOS has none at all): its card moves items.
    element.draggable = !isTouch() && view.inReach && !stuck;

    element.addEventListener('dragstart', (event) => {
      event.dataTransfer?.setData(dragType(this.side), item.id);
      this.tooltip.hide();
    });
    element.addEventListener('dblclick', () => {
      if (isTouch()) return;
      this.moveWhole(item.id);
    });
    element.addEventListener('click', () => {
      this.select(element, item);
      if (isTouch()) this.pin(element, item, template, view);
    });
    // A tap also fires emulated mouse events: they would open the cursor's
    // card, then close the pinned one as the finger reaches its buttons.
    element.addEventListener('mouseenter', (event) => {
      if (isTouch()) return;
      this.tooltip.show(template, item, view.wearer, { x: event.clientX, y: event.clientY });
    });
    element.addEventListener('mousemove', (event) => {
      if (isTouch()) return;
      this.tooltip.moveTo({ x: event.clientX, y: event.clientY });
    });
    element.addEventListener('mouseleave', () => {
      if (isTouch()) return;
      this.tooltip.hide();
    });
    return element;
  }

  /** Picks the item in place: no redraw, so a double-click still finds its element. */
  private select(element: HTMLElement, item: ItemInstance): void {
    this.unselect();
    this.selected = item.id;
    element.classList.add('item--selected');
    this.handlers.onSelect();
  }

  /** The touch card: the stack, and for a stack one, sent over by its buttons. */
  private pin(
    element: HTMLElement,
    item: ItemInstance,
    template: ItemTemplate,
    view: StashGridView,
  ): void {
    const verb = this.side === 'chest' ? uiText('Retirer', 'stash') : uiText('Déposer');
    const actions: TooltipAction[] = [];
    const action = (label: string, quantity: number): void => {
      if (stashMoveBlock(this.side, item, template, quantity, view.target, view.inReach)) return;
      actions.push({
        label,
        run: () => {
          this.handlers.onMove(item.id, quantity);
        },
      });
    };
    action(verb, item.quantity);
    if (item.quantity > 1) action(uiFormat('{verb} {count}', { verb, count: uiNumber(1) }), 1);
    this.tooltip.pin(template, item, view.wearer, element, actions);
  }

  /** The whole grid takes a drop from the other side only: its own does not light it up. */
  private bindDropTarget(): void {
    const grid = this.element;
    const type = dragType(this.side === 'chest' ? 'bag' : 'chest');
    grid.addEventListener('dragover', (event) => {
      if (event.dataTransfer?.types.includes(type) !== true) return;
      event.preventDefault();
      grid.classList.add('slot--over');
    });
    grid.addEventListener('dragleave', (event) => {
      if (event.relatedTarget instanceof Node && grid.contains(event.relatedTarget)) return;
      grid.classList.remove('slot--over');
    });
    grid.addEventListener('drop', (event) => {
      grid.classList.remove('slot--over');
      const itemId = event.dataTransfer?.getData(type);
      if (itemId === undefined || itemId === '') return;
      event.preventDefault();
      // The chest keeps no order (ADR 0079); the bag is tidied in the inventory.
      this.handlers.onReceive(itemId);
    });
  }
}
